import client from './client';
import { isPageKey, PAGE_KEYS, PAGE_ROUTES, PageKey } from '../router/pageRegistry';

/** One sidebar row standing for several pages. The label and icon are ours. */
export interface NavGroupInfo {
  groupKey: string;
  navSection: string;
  displayOrder: number;
}

/** A page the caller may see, and where in the navigation it renders. */
export interface NavPageInfo {
  pageKey: PageKey;
  routePath: string;
  navSection: string;
  displayOrder: number;
  /** Null for a page that is a row of its own. */
  navGroupKey: string | null;
}

/**
 * What the signed-in user may see, and how it is arranged.
 *
 * Two answers from one resolution, because they have to agree. `pages` is what
 * the router asks — may this person open this route — and `layout` is what the
 * sidebar renders. One request was not enough on its own: the endpoint resolved
 * access twice, so a grant changed in between could leave a page routable while
 * the navigation still grouped it under a row that had gone.
 */
export interface MyPages {
  pages: PageKey[];
  groups: NavGroupInfo[];
  layout: NavPageInfo[];
}

type MyPagesPayload = {
  pages?: string[];
  groups?: NavGroupInfo[];
  layout?: (Omit<NavPageInfo, 'pageKey'> & { pageKey: string })[];
};

/**
 * A navigation with no grouping in it: every page held is a row of its own.
 *
 * The honest answer when nothing told us how to arrange them — the request
 * failed, or the server predates the nav_group table. A reduced sidebar is
 * usable; an empty one is indistinguishable from a broken build, and the shell
 * can build no row at all from a bare list of keys (it has no path, no order
 * and no section to render one with).
 *
 * One section, so no dividers appear where we do not know a boundary exists.
 * That stays right now that administration pages arrive here too — a global
 * admin whose request failed falls back to ADMIN_CORE_PAGE_KEYS, and the
 * section label they are given is unused except by the divider rule.
 * `PAGE_KEYS` order stands in for `display_order`, which it already mirrors.
 */
export const fallbackNavigation = (
  pages: readonly PageKey[],
): { groups: NavGroupInfo[]; layout: NavPageInfo[] } => {
  const held = new Set(pages);
  return {
    groups: [],
    layout: PAGE_KEYS.filter((key) => held.has(key)).map((key, index) => ({
      pageKey: key,
      routePath: PAGE_ROUTES[key],
      navSection: 'member',
      displayOrder: index,
      navGroupKey: null,
    })),
  };
};

/**
 * Pages the signed-in user may see.
 *
 * Unknown keys are dropped rather than passed through: a page the server knows
 * about but this build does not has no route to render, and keeping it would
 * only let it reach code that types it as a PageKey. The same filter applies to
 * the layout, so a half-shipped page cannot put a row in the sidebar that
 * nothing can open.
 */
export const getMyPages = async (): Promise<MyPages> => {
  const response = await client.get<MyPagesPayload>('/pages/me');
  const { pages = [], groups = [], layout = [] } = response.data ?? {};

  const known = pages.filter(isPageKey);
  const rows = layout.filter((page): page is NavPageInfo =>
    isPageKey(page.pageKey),
  );

  return rows.length > 0
    ? { pages: known, groups, layout: rows }
    : { pages: known, ...fallbackNavigation(known) };
};
